import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Sparkles, Hand, TimerReset } from 'lucide-react';
import { AiCarRentalContent } from '@/pages/AiCarRentalPage';
import { KioskContext } from '@/components/ai/kioskContext';
import { CoBrandLockup } from '@/components/ai/AiSections';
import { closeChatWidget, warmChatWidget } from '@/components/ai/openChatWidget';
import { useIdleReset } from '@/hooks/useIdleReset';

const EC_GREEN = '#159A47';
const WARN_MS = 20000;
const LC_SESSION_KEY = /^v2_contact_session_/;

const clearChatSession = () => Object.keys(localStorage).filter((k) => LC_SESSION_KEY.test(k)).forEach((k) => localStorage.removeItem(k));

// Flags the session once the LeadConnector panel has been opened (chat-widget[data-active="true"]).
function useChatUsed(session) {
  const used = useRef(false);
  useEffect(() => {
    used.current = false;
    let observer = null;
    const attach = () => {
      const cw = document.querySelector('chat-widget');
      if (!cw) return false;
      const check = () => { if (cw.getAttribute('data-active') === 'true') used.current = true; };
      observer = new MutationObserver(check);
      observer.observe(cw, { attributes: true, attributeFilter: ['data-active'] });
      check();
      return true;
    };
    const poll = attach() ? null : setInterval(() => { if (attach()) clearInterval(poll); }, 500);
    return () => { if (poll) clearInterval(poll); observer?.disconnect(); };
  }, [session]);
  return used;
}

function AttractScreen({ onStart }) {
  return (
    <button type="button" data-testid="kiosk-attract" onClick={onStart} className="fixed inset-0 z-[70] flex flex-col items-center justify-center overflow-hidden text-center text-white" style={{ backgroundColor: '#0E6B33' }} aria-label="Tap to start">
      <img src="/ai/hero-dubai.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" style={{ objectPosition: 'center 58%' }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)', backgroundSize: '28px 28px' }} aria-hidden="true" />
      <div className="relative max-w-4xl px-6">
        <CoBrandLockup light />
        <div className="mt-10"><span className="inline-flex items-center gap-2 rounded-full bg-white/[0.14] px-4 py-2 font-heading font-bold text-xs tracking-[0.18em] uppercase ring-1 ring-white/25"><Sparkles className="w-4 h-4 text-[#7CFC9A]" /> Arabian Travel Market 2026</span></div>
        <h1 className="mt-6 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">Discover what truly <span className="text-[#7CFC9A]">AI assisted</span> car rental feels like.</h1>
        <p className="mt-6 mx-auto max-w-2xl font-body text-lg sm:text-xl text-white/85">Tell the Europcar AI Concierge about your trip and watch it find the car, the location and the price that fit you, in seconds.</p>
        <span className="mt-12 inline-flex min-h-[72px] items-center gap-3 rounded-full bg-white px-10 py-5 font-heading font-black text-xl shadow-[0_24px_60px_rgba(0,0,0,0.25)] animate-pulse" style={{ color: '#0E6B33' }}><Hand className="w-6 h-6" /> Tap anywhere to start</span>
        <p className="mt-8 font-body text-sm text-white/60">Free to try · No sign-up · English &amp; Arabic · Eurogulf Mobility Group × Europcar Car Rental: Dubai &amp; NE</p>
      </div>
    </button>
  );
}

function IdleWarning({ remaining, onStay }) {
  return (
    <div data-testid="kiosk-idle-warning" role="dialog" aria-live="assertive" className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-6 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 text-center shadow-2xl">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#E8F5EC]" style={{ color: EC_GREEN }}><TimerReset className="w-8 h-8" /></span>
        <h2 className="mt-5 font-heading font-black text-2xl text-[#121212]">Still there?</h2>
        <p className="mt-2 font-body text-base text-[#666666]">This screen resets for the next visitor in <span data-testid="kiosk-idle-countdown" className="font-heading font-black text-[#121212]">{remaining}</span> seconds.</p>
        <button type="button" data-testid="kiosk-stay-btn" onClick={onStay} className="mt-6 inline-flex min-h-[56px] w-full items-center justify-center rounded-full px-8 font-heading font-bold text-base text-white" style={{ backgroundColor: EC_GREEN }}>I&apos;m still here</button>
      </div>
    </div>
  );
}

// Staff exit: five quick taps on the invisible top-left corner leave kiosk mode.
function ExitHotspot({ onExit }) {
  const taps = useRef([]);
  const tap = () => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < 3000), now];
    if (taps.current.length >= 5) { taps.current = []; onExit(); }
  };
  return <button type="button" data-testid="kiosk-exit-hotspot" onClick={tap} aria-label="Staff: exit kiosk mode" className="fixed left-0 top-0 z-[90] h-14 w-14 opacity-0" />;
}

export default function KioskPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const idleMs = Math.max(30, Number(params.get('idle')) || 150) * 1000;
  const [started, setStarted] = useState(false);
  const [session, setSession] = useState(0);
  const chatUsed = useChatUsed(session);

  useEffect(() => {
    document.title = 'AI Car Rental Concierge · Kiosk | Eurogulf Mobility Group × Europcar';
    document.documentElement.classList.add('kiosk-mode');
    warmChatWidget();
    return () => document.documentElement.classList.remove('kiosk-mode');
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('kiosk-attract', !started);
    return () => document.documentElement.classList.remove('kiosk-attract');
  }, [started]);

  const start = () => {
    document.documentElement.requestFullscreen?.({ navigationUI: 'hide' })?.catch?.(() => {});
    setStarted(true);
  };

  const reset = useCallback(() => {
    closeChatWidget();
    clearChatSession();
    if (chatUsed.current && navigator.onLine) { window.location.replace(window.location.pathname + window.location.search); return; }
    setSession((s) => s + 1);
    setStarted(false);
  }, [chatUsed]);

  useLayoutEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [session]);

  const exit = () => {
    document.exitFullscreen?.()?.catch?.(() => {});
    navigate('/ai-car-rental');
  };

  const { remaining, stay } = useIdleReset({ enabled: started, idleMs, warnMs: WARN_MS, onReset: reset });

  return (
    <KioskContext.Provider value>
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
      <div data-testid="kiosk-page" data-started={started} className="min-h-screen bg-white">
        <AiCarRentalContent key={session} />
        {!started && <AttractScreen onStart={start} />}
        {started && remaining !== null && <IdleWarning remaining={remaining} onStay={stay} />}
        <ExitHotspot onExit={exit} />
      </div>
    </KioskContext.Provider>
  );
}
